import { chunkPassage, findAll, readPassage, readVisibleText } from './visible-text'

enum MatchStatus {
  FULL = 'full',
  PARTIAL = 'partial',
  NONE = 'none',
}

export type MatchStatusSummary = {
  passageCount: number
  fullCount: number
  partialCount: number
  noneCount: number
  matchedChunkCount: number
  totalChunkCount: number
  /** Passages whose text appears more than once on the page, where the viewer has to pick a place. */
  repeatedPassageCount: number
}

/**
 * Measure how well the passages are highlighted in rendered HTML, from what is actually painted.
 *
 * Each passage is split into ten-word chunks. A chunk counts as matched when its text appears
 * inside one painted stretch of the page. A passage is full when every chunk matched, partial when
 * some did, and none otherwise. Reading the paint rather than a matcher's own report lets the old
 * and new highlighters be compared the same way.
 */
export function measureMatchStatus(root: Node, passages: string[]): MatchStatusSummary {
  const text = readVisibleText(root)
  const paintedNodes = new Map<Text, boolean>()
  const isPainted = (node: Text | undefined): boolean => {
    if (!node) return false
    let painted = paintedNodes.get(node)
    if (painted === undefined) {
      painted = Boolean(node.parentElement?.closest('mark'))
      paintedNodes.set(node, painted)
    }
    return painted
  }
  const paintedUnits: string[] = []
  for (let unit = 0; unit < text.key.length; unit++) paintedUnits.push(isPainted(text.chars[text.keyToChar[unit]].location?.node) ? text.key[unit] : '\u0000')
  const paintedKey = paintedUnits.join('')

  const summary: MatchStatusSummary = {
    passageCount: passages.length,
    fullCount: 0,
    partialCount: 0,
    noneCount: 0,
    matchedChunkCount: 0,
    totalChunkCount: 0,
    repeatedPassageCount: 0,
  }
  for (const passage of passages.map((passage) => readPassage(passage))) {
    const chunks = chunkPassage(passage)
    const matched = chunks.filter((chunk) => paintedKey.includes(chunk.key)).length
    const status = matched > 0 && matched === chunks.length ? MatchStatus.FULL : matched > 0 ? MatchStatus.PARTIAL : MatchStatus.NONE
    if (status === MatchStatus.FULL) summary.fullCount++
    else if (status === MatchStatus.PARTIAL) summary.partialCount++
    else summary.noneCount++
    summary.matchedChunkCount += matched
    summary.totalChunkCount += chunks.length
    if (findAll(text.key, passage.key).length > 1) summary.repeatedPassageCount++
  }
  return summary
}
